import React, { useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import RegisterForm from './RegisterForm/RegisterForm'
import InformationContainer from './InformationContainer/InformationContainer'
import Grid from '@mui/material/Grid'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import { Typography, Button, Box } from '@mui/material'
import { Home, LogIn } from 'react-feather'
import useFetchUserByCheckoutSessionId from '../../../../api/onboarding/useFetchUserByCeckoutSessionId'
import { useAppDispatch } from '../../../../store/utils/hooks'
import { actionSaveOnboardingData } from '../../../../store/slices/user/userSlice'
import useFirebaseAuthentication from '../../../../utils/Firebase/hooks/useFirebaseAuthentication'
import { User } from 'firebase/auth'
import { userSignOut } from '../../../../utils/Firebase/methods'
import { useNavigate } from 'react-router-dom'
import { Login } from '@mui/icons-material'
import { LOGIN_PATH } from '../../../../routes/LoginRoutes'
import { HOME_PATH } from '../../../../routes/HomeRoutes'

export default function RegisterSection() {
	const dispatch = useAppDispatch()
	const [searchParams] = useSearchParams()
	const checkoutSessionId = searchParams.get('session_id')
	const { loading, authUser } = useFirebaseAuthentication() as {
		loading: boolean
		authUser: (User & { reloadUserInfo?: { localId: string } }) | null
	}

	const { data: checkoutData, isLoading } = useFetchUserByCheckoutSessionId(checkoutSessionId)
	const navigate = useNavigate()
	const [showSecondMessage, setShowSecondMessage] = React.useState(false)

	const wrongUser = !!(
		checkoutData?.user_id &&
		authUser?.reloadUserInfo?.localId &&
		authUser?.reloadUserInfo?.localId != checkoutData?.user_id
	)

	const getRegisterForm = (wrongUser: boolean) => {
		if (wrongUser) {
			return (
				<Stack gap="16px" width="100%" maxWidth="360px">
					<Typography variant="h5" sx={{ fontWeight: 700 }}></Typography>
					<Typography variant="body1" sx={{ fontWeight: 400 }}>
						Switch to the account associated with {checkoutData?.email}.
					</Typography>

					<Button
						variant="contained"
						onClick={async () => {
							await userSignOut()
							navigate(LOGIN_PATH)
						}}
						startIcon={<LogIn />}
						sx={{ height: '56px' }}
						fullWidth
					>
						Logout
					</Button>
				</Stack>
			)
		} else if (!authUser && checkoutData?.user_id) {
			return (
				<Stack gap="16px" width="100%" maxWidth="360px">
					<Typography variant="h5" sx={{ fontWeight: 700 }}>
						Login to your account
					</Typography>
					<Button
						component={Link}
						to={LOGIN_PATH}
						variant="contained"
						startIcon={<Login />}
						sx={{ height: '56px' }}
						fullWidth
					>
						Login
					</Button>
				</Stack>
			)
		} else if (!authUser || !checkoutData) {
			return (
				<RegisterForm
					prefillData={
						checkoutData
							? {
									email: checkoutData.email,
									phoneNumber: checkoutData.phone_number,
								}
							: undefined
					}
				/>
			)
		} else {
			return (
				<Stack gap="16px" width="100%" maxWidth="360px">
					<Typography variant="h5" sx={{ fontWeight: 700 }}>
						Your account is ready!
					</Typography>
					<Button
						component={Link}
						to={HOME_PATH}
						variant="contained"
						startIcon={<Home />}
						sx={{ height: '56px' }}
						fullWidth
					>
						Go to Home
					</Button>
				</Stack>
			)
		}
	}

	useEffect(() => {
		if (checkoutData) {
			console.log('checkoutData', checkoutData)
			if (checkoutData.name.includes(' ')) {
				const firstName = checkoutData.name.split(' ')[0]
				const lastName = checkoutData.name.split(' ')[1]
				dispatch(
					actionSaveOnboardingData({
						email: checkoutData.email,
						phoneNumber: checkoutData.phone_number,
						firstName: firstName,
						lastName: lastName,
					}),
				)
			} else {
				dispatch(
					actionSaveOnboardingData({
						email: checkoutData.email,
						phoneNumber: checkoutData.phone_number,
						firstName: checkoutData.name,
					}),
				)
			}
		}
	}, [dispatch, checkoutData])

	useEffect(() => {
		if (loading || isLoading) {
			const timer = setTimeout(() => {
				setShowSecondMessage(true)
			}, 3000)
			return () => clearTimeout(timer)
		} else {
			setShowSecondMessage(false)
		}
	}, [loading, isLoading])

	if (loading || isLoading) {
		return (
			<Stack justifyContent="center" height="100vh" alignItems="center" gap={2}>
				<Typography
					variant="h5"
					sx={{
						fontWeight: 700,
						opacity: showSecondMessage ? 0.3 : 1,
						transition: 'opacity 2s ease-in-out',
					}}
				>
					Welcome to the future.
				</Typography>
				<Typography
					variant="h6"
					sx={{
						opacity: showSecondMessage ? 1 : 0,
						transition: 'opacity 2s ease-in-out',
						height: showSecondMessage ? 'auto' : 0,
						overflow: 'hidden',
					}}
				>
					Just a moment while we get your account ready...
				</Typography>
				<CircularProgress />
			</Stack>
		)
	}

	return (
		<Grid container justifyContent="center" spacing={{ xs: 2, md: 4 }}>
			<Grid item xs={12} md={5} lg={4} sx={{ display: 'flex', justifyContent: 'center' }}>
				<InformationContainer
					isCheckoutFlow={!!checkoutSessionId}
					loggedIn={!!authUser}
					checkoutData={checkoutData}
					wrongUser={wrongUser}
				/>
			</Grid>
			<Grid item xs={12} md={5} lg={4} sx={{ display: 'flex', justifyContent: 'center' }}>
				{getRegisterForm(wrongUser)}
			</Grid>
		</Grid>
	)
}
